import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import Activity from './Activity';
import Referral from './Referral';
import '../styles/Dashboard.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeView, setActiveView] = useState('activity');
  const [locationPermission, setLocationPermission] = useState(null);
  const [isTracking, setIsTracking] = useState(false);
  const [newLocation, setNewLocation] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const locationIntervalRef = useRef(null);
  const trackingStartedRef = useRef(false);

  const getAndSaveLocation = () => {
    if (!('geolocation' in navigator)) {
      console.warn('Geolocation not available');
      return;
    }

    console.log('Attempting to get current location...');
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        console.log(`Location obtained at ${new Date().toLocaleTimeString()}:`, latitude, longitude);

        try {
          const response = await axios.post(
            `${API_BASE_URL}/location/save`,
            { latitude, longitude },
            { withCredentials: true }
          );
          console.log(`Location saved successfully at ${new Date().toLocaleTimeString()}:`, response.data);
          // Pass the new location to Activity component instead of refetching
          if (response.data.success && response.data.location) {
            setNewLocation(response.data.location);
          }
        } catch (error) {
          console.error('Error saving location:', error);
          // Only stop tracking if it's an authentication/permission error
          if (error.response?.status === 403 || error.response?.status === 401) {
            console.error('Authentication error - stopping location tracking');
            trackingStartedRef.current = false;
            setLocationPermission('denied');
            setIsTracking(false);
            if (locationIntervalRef.current) {
              clearInterval(locationIntervalRef.current);
              locationIntervalRef.current = null;
            }
          }
          // For other errors, continue tracking (network issues, etc.)
        }
      },
      (error) => {
        console.error(`Error getting location at ${new Date().toLocaleTimeString()}:`, error);
        // Only stop tracking if permission is explicitly denied
        if (error.code === error.PERMISSION_DENIED) {
          console.error('Location permission denied - stopping tracking');
          setLocationPermission('denied');
          setIsTracking(false);
          if (locationIntervalRef.current) {
            clearInterval(locationIntervalRef.current);
            locationIntervalRef.current = null;
          }
        } else {
          // For timeout or other errors, log but continue tracking
          console.warn('Location fetch failed but will retry on next interval');
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 15000, // Increased timeout to 15 seconds
        maximumAge: 60000, // Accept cached location up to 1 minute old
      }
    );
  };

  const startLocationTracking = () => {
    // Prevent multiple starts
    if (trackingStartedRef.current && locationIntervalRef.current) {
      console.log('Location tracking already started, skipping...');
      return;
    }

    // Clear any existing interval (safety check)
    if (locationIntervalRef.current) {
      console.log('Clearing existing location tracking interval');
      clearInterval(locationIntervalRef.current);
      locationIntervalRef.current = null;
    }

    trackingStartedRef.current = true;
    setIsTracking(true);
    console.log('Starting location tracking...');
    
    // Get location immediately
    getAndSaveLocation();

    // Then get location every minute (60000 ms = 60 seconds = 1 minute)
    locationIntervalRef.current = setInterval(() => {
      const now = new Date().toLocaleTimeString();
      console.log(`[${now}] Scheduled location fetch - interval is running`);
      getAndSaveLocation();
    }, 60000);
    
    console.log(`Location tracking scheduled at ${new Date().toLocaleTimeString()} - will fetch every 60 seconds`);
    console.log('Interval ID:', locationIntervalRef.current);
  };

  useEffect(() => {
    // Check location permission status
    if ('geolocation' in navigator) {
      // Try to query permissions (may not be supported in all browsers)
      if (navigator.permissions && navigator.permissions.query) {
        navigator.permissions.query({ name: 'geolocation' }).then((result) => {
          setLocationPermission(result.state);
          if (result.state === 'granted') {
            startLocationTracking();
          }
          // Listen for permission changes
          result.addEventListener('change', () => {
            setLocationPermission(result.state);
            if (result.state === 'granted') {
              startLocationTracking();
            } else if (result.state === 'denied') {
              trackingStartedRef.current = false;
              setIsTracking(false);
              if (locationIntervalRef.current) {
                clearInterval(locationIntervalRef.current);
                locationIntervalRef.current = null;
              }
            }
          });
        }).catch(() => {
          // Permissions API not supported, will request on button click
          setLocationPermission('prompt');
        });
      } else {
        // Permissions API not supported
        setLocationPermission('prompt');
      }
    } else {
      setLocationPermission('not-supported');
    }

    // Cleanup interval on unmount
    return () => {
      if (locationIntervalRef.current) {
        clearInterval(locationIntervalRef.current);
        locationIntervalRef.current = null;
      }
      trackingStartedRef.current = false;
    };
  }, []);

  const requestLocationPermission = () => {
    if (!('geolocation' in navigator)) {
      alert('Geolocation is not supported by your browser.');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      () => {
        // Permission granted
        setLocationPermission('granted');
        startLocationTracking();
      },
      (error) => {
        // Permission denied or error
        if (error.code === error.PERMISSION_DENIED) {
          setLocationPermission('denied');
          alert('Location permission is required to track your activity. Please enable it in your browser settings.');
        } else {
          setLocationPermission('error');
          console.error('Error getting location:', error);
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 0,
      }
    );
  };

  const handleLogout = async () => {
    // Clear location tracking interval
    if (locationIntervalRef.current) {
      clearInterval(locationIntervalRef.current);
      locationIntervalRef.current = null;
    }
    
    // Reset tracking state
    trackingStartedRef.current = false;
    setIsTracking(false);
    
    await logout();
    navigate('/', { replace: true });
  };

  const renderContent = () => {
    switch (activeView) {
      case 'activity':
        return <Activity newLocation={newLocation} />;
      case 'referral':
        return <Referral />;
      default:
        return <Activity newLocation={newLocation} />;
    }
  };

  return (
    <div className="dashboard-layout">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)}></div>
      )}

      {/* Sidebar */}
      <aside className={`dashboard-sidebar ${sidebarOpen ? 'sidebar-open' : ''}`}>
        <div className="sidebar-header">
          <h2>Earthpeace</h2>
          <button className="sidebar-close-btn" onClick={() => setSidebarOpen(false)}>×</button>
        </div>
        <nav className="sidebar-nav">
          <button
            className={`sidebar-nav-item ${activeView === 'activity' ? 'active' : ''}`}
            onClick={() => {
              setActiveView('activity');
              setSidebarOpen(false);
            }}
          >
            Activity
          </button>
          <button
            className={`sidebar-nav-item ${activeView === 'referral' ? 'active' : ''}`}
            onClick={() => {
              setActiveView('referral');
              setSidebarOpen(false);
            }}
          >
            Referral
          </button>
        </nav>
      </aside>

      {/* Main Content */}
      <main className="dashboard-main">
        {/* Header */}
        <header className="dashboard-header">
          <div className="dashboard-header-left">
            <button className="sidebar-toggle-btn" onClick={() => setSidebarOpen(true)}>
              ☰
            </button>
            <h1 className="dashboard-header-title">
              {activeView === 'activity' ? 'Activity' : activeView === 'referral' ? 'Referral' : 'Dashboard'}
            </h1>
          </div>
          <div className="dashboard-header-actions">
            {locationPermission !== 'granted' && locationPermission !== 'denied' && (
              <button
                className="location-permission-button"
                onClick={requestLocationPermission}
              >
                Enable Location Tracking
              </button>
            )}
            {locationPermission === 'granted' && isTracking && (
              <span className="tracking-status">📍 Tracking Active</span>
            )}
            <button className="logout-button" onClick={handleLogout}>
              Logout
            </button>
          </div>
        </header>

        {/* Content Area */}
        <div className="dashboard-content-area">
          {renderContent()}
        </div>
      </main>
    </div>
  );
}

export default Dashboard;